import { useEffect, useState } from 'preact/hooks';
import { matchPrices, summarise, type PriceHistoryBody } from '../../shared/prices/history';
import { normaliseItem } from '../../shared/quotes/rules';
import { useI18n } from '../i18n';
import { ApiError, apiGet } from '../lib/api';
import { formatDate } from '../lib/dates';

// Price history (D72): past prices of one category, searched by item. Procurement and CFO.

type Load =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'error'; forbidden: boolean; reference: string | null }
  | { kind: 'ok'; data: PriceHistoryBody };

const MAX_SHOWN = 200;
const money = (minor: number) =>
  new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(
    minor / 100,
  );
const qtyText = (q: number) =>
  new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(q);

export function PriceHistoryPage() {
  const { t, lang } = useI18n();
  const [category, setCategory] = useState('');
  const [categories, setCategories] = useState<PriceHistoryBody['categories']>([]);
  const [load, setLoad] = useState<Load>({ kind: 'loading' });
  const [q, setQ] = useState('');

  useEffect(() => {
    let live = true;
    setLoad({ kind: 'loading' });
    apiGet<PriceHistoryBody>(`/api/prices?category=${encodeURIComponent(category)}`)
      .then((data) => {
        if (!live) return;
        setCategories(data.categories);
        setLoad(category ? { kind: 'ok', data } : { kind: 'idle' });
      })
      .catch((err: unknown) => {
        if (!live) return;
        const e = err instanceof ApiError ? err : null;
        setLoad({
          kind: 'error',
          forbidden: e?.code === 'forbidden',
          reference: e?.correlationId ?? null,
        });
      });
    return () => {
      live = false;
    };
  }, [category]);

  const rows = load.kind === 'ok' ? matchPrices(load.data.rows, q, normaliseItem) : [];
  const sum = summarise(rows);
  const catName = (c: PriceHistoryBody['categories'][number]) =>
    lang === 'ar' ? c.nameAR || c.nameEN : c.nameEN;

  return (
    <div class="stack">
      <section class="card stack" aria-labelledby="ph-title">
        <h2 id="ph-title" class="card__title">
          {t('ph.title')}
        </h2>
        <p class="caption">{t('ph.intro')}</p>
        <div class="queue-filters">
          <div class="field">
            <label for="ph-cat">{t('req.f.category')}</label>
            <select
              id="ph-cat"
              value={category}
              onChange={(e) => setCategory((e.target as HTMLSelectElement).value)}
            >
              <option value="">{t('ph.pickCategory')}</option>
              {categories.map((c) => (
                <option key={c.id} value={c.id}>
                  {catName(c)}
                </option>
              ))}
            </select>
          </div>
          <div class="field">
            <label for="ph-q">{t('ph.search')}</label>
            <input
              id="ph-q"
              type="search"
              value={q}
              disabled={load.kind !== 'ok'}
              onInput={(e) => setQ((e.target as HTMLInputElement).value)}
            />
          </div>
        </div>
        {load.kind === 'loading' && <p aria-busy="true">{t('req.loading')}</p>}
        {load.kind === 'idle' && <p>{t('ph.idle')}</p>}
        {load.kind === 'error' && (
          <div role="alert">
            <p>{t(load.forbidden ? 'review.error.forbidden' : 'req.loadError')}</p>
            {load.reference && (
              <p class="caption" dir="ltr">
                {t('error.reference', { ref: load.reference })}
              </p>
            )}
          </div>
        )}
        {load.kind === 'ok' && load.data.capped && (
          <p class="caption" role="status">
            {t('ph.capped')}
          </p>
        )}
      </section>
      {load.kind === 'ok' && (
        <>
          <ul class="tiles dash-tiles" aria-label={t('ph.summary')} aria-live="polite">
            {(
              [
                ['ph.count', String(sum.count), ''],
                ['ph.low', sum.minSAR === null ? '—' : `SAR ${money(sum.minSAR)}`, ''],
                ['ph.avg', sum.avgSAR === null ? '—' : `SAR ${money(sum.avgSAR)}`, ''],
                ['ph.high', sum.maxSAR === null ? '—' : `SAR ${money(sum.maxSAR)}`, ''],
                [
                  'ph.last',
                  sum.last ? `${sum.last.currency} ${money(sum.last.unitPrice)}` : '—',
                  sum.last?.date ? formatDate(sum.last.date, lang) : '',
                ],
              ] as const
            ).map(([label, value, sub]) => (
              <li key={label} class="tile">
                <span class="caption">{t(label)}</span>
                <span class="tile__value" dir="ltr">
                  {value}
                </span>
                <span>{sub}</span>
              </li>
            ))}
          </ul>
          <section class="card" aria-label={t('ph.table')}>
            {rows.length === 0 ? (
              <p>{t('ph.none')}</p>
            ) : (
              <>
                <p class="caption">
                  {t('sup.history.count', {
                    shown: String(Math.min(rows.length, MAX_SHOWN)),
                    total: String(rows.length),
                  })}
                </p>
                <div class="table-wrap">
                  <table class="table">
                    <thead>
                      <tr>
                        <th scope="col">{t('ph.col.item')}</th>
                        <th scope="col">{t('inv.col.supplier')}</th>
                        <th scope="col" class="num">
                          {t('ph.col.qty')}
                        </th>
                        <th scope="col" class="num">
                          {t('ph.col.unit')}
                        </th>
                        <th scope="col">{t('ph.col.where')}</th>
                        <th scope="col">{t('pol.col.date')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.slice(0, MAX_SHOWN).map((r, i) => (
                        <tr key={`${r.poNo}-${i}`}>
                          <td>{r.item}</td>
                          <td>{r.supplierName || '—'}</td>
                          <td class="num" dir="ltr">
                            {qtyText(r.qty)}
                          </td>
                          <td class="num" dir="ltr">
                            {r.currency} {money(r.unitPrice)}
                          </td>
                          <td>
                            <span dir="ltr">{r.poNo || r.prNo || '—'}</span>
                            <span class="caption cell-sub">
                              {[r.department, r.vertical].filter(Boolean).join(' · ')}
                            </span>
                          </td>
                          <td>{r.date ? formatDate(r.date, lang) : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </section>
        </>
      )}
    </div>
  );
}
